import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectSession,
} from '../../store/selectors/authSelectors';
import {
  Briefcase,
  Plus,
  Search,
} from 'react-feather';
import styles from './DashboardPage.module.css';
import ErrorModal from '../../components/Modals/ErrorModal';
import JobEditorModal from './JobEditorModal';
import TabBar from '../../components/Tabs/TabBar';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import MainLayout from '../../components/Layout/MainLayout';
import { useDashboardJobs } from './hooks/useDashboardJobs';
import { DashboardJob, CreateJobPayload, UpdateJobPayload } from './types';
import JobCard from './JobCard';

interface DashboardPageProps {
  onLogout: () => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const session = useSelector(selectSession);
  const user = session?.user;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars


  const {
    jobs,
    loading,
    createJob,
    updateJob,
    deleteJob,
  } = useDashboardJobs();

  const [activeFilter, setActiveFilter] = useState<'open' | 'archived'>('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [jobToEdit, setJobToEdit] = useState<DashboardJob | null>(null);
  const [jobToDelete, setJobToDelete] = useState<DashboardJob | null>(null);

  // Error Modal State
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Filter jobs based on active tab and search query
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesFilter =
        activeFilter === 'open'
          ? job.status === 'OPEN'
          : job.status === 'CLOSED' || job.status === 'PAUSED';

      const matchesSearch =
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.outlet_name.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesFilter && matchesSearch;
    });
  }, [jobs, activeFilter, searchQuery]);

  const handleCreateJob = () => {
    setJobToEdit(null);
    setIsEditorOpen(true);
  };

  const handleEditJob = (job: DashboardJob) => {
    setJobToEdit(job);
    setIsEditorOpen(true);
  };

  const handleDeleteClick = (job: DashboardJob) => {
    setJobToDelete(job);
  };

  const handleConfirmDelete = async () => {
    if (jobToDelete) {
      const result = await deleteJob({ job_id: jobToDelete.job_id });

      if (result.success) {
        setJobToDelete(null);
      } else {
        // Handle error
        setJobToDelete(null); // Close confirmation modal

        const errorObj = typeof result === 'object' ? result.error : null;
        let message = 'Failed to delete job.';

        if (errorObj) {
          message = 'Cannot delete job with accepted applications';
        }
        setErrorMessage(message);
        setIsErrorModalOpen(true);
      }
    }
  };

  const handleSaveJob = async (jobData: any) => {
    if (jobToEdit) {
      const payload: UpdateJobPayload = {
        job_id: jobToEdit.job_id,
        title: jobData.title,
        job_description: jobData.description,
        salary_range: `${jobData.minSalary}-${jobData.maxSalary}`,
        expected_hires: Number(jobData.expectedHires),
        tentative_joining: jobData.tentative_joining,
        outlet_id: jobData.outlet_id,
      };
      const success = await updateJob(payload);
      if (success) {
        setIsEditorOpen(false);
      }
    } else {
      const payload: CreateJobPayload = {
        title: jobData.title,
        job_description: jobData.description,
        salary_range: `${jobData.minSalary}-${jobData.maxSalary}`,
        expected_hires: Number(jobData.expectedHires),
        tentative_joining: jobData.tentative_joining,
        outlet_id: jobData.outlet_id,
      };
      const success = await createJob(payload);
      if (success) {
        setIsEditorOpen(false);
      }
    }
  };

  const handleNavigateToATS = (job: DashboardJob) => {
    navigate(`/ats/${job.job_id}`, { state: { job } });
  };

  const renderContent = () => {
    if (loading && jobs.length === 0) {
      return (
        <div className={styles.loadingState} data-testid="dashboard-loading">
          <div className={styles.spinner}></div>
          <p>Loading jobs...</p>
        </div>
      );
    }

    if (filteredJobs.length === 0) {
      return (
        <div className={styles.emptyState} data-testid="dashboard-empty">
          <div className={styles.emptyIcon}>
            <Briefcase size={48} />
          </div>
          <h3>No jobs found</h3>
          <p>
            {searchQuery
              ? `No jobs match "${searchQuery}"`
              : activeFilter === 'open'
                ? "You haven't posted any jobs yet."
                : 'No archived jobs found.'}
          </p>
          {activeFilter === 'open' && !searchQuery && (
            <button className={styles.createJobButton} onClick={handleCreateJob} data-testid="create-first-job-button">
              <Plus size={18} />
              Create Your First Job
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.jobsGrid} data-testid="dashboard-jobs-grid">
        {filteredJobs.map((job) => (
          <JobCard
            key={job.job_id}
            job={job}
            onClick={() => handleNavigateToATS(job)}
            onEdit={(e) => {
              e.stopPropagation();
              handleEditJob(job);
            }}
            onDelete={(e) => {
              e.stopPropagation();
              handleDeleteClick(job);
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.pageTitle} data-testid="dashboard-title">Dashboard</h1>
            <p className={styles.pageSubtitle} data-testid="dashboard-subtitle">Manage your job postings and applications</p>
          </div>
          <button className={styles.createJobButton} onClick={handleCreateJob} data-testid="post-job-button">
            <Plus size={18} />
            Post New Job
          </button>
        </div>

        <div className={styles.filters}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder="Search jobs..."
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-jobs-input"
            />
          </div>
        </div>

        <TabBar
          tabs={[
            { id: 'open', label: 'Active Jobs ', count: jobs.filter(j => j.status === 'OPEN').length },
            { id: 'archived', label: 'Archived ', count: jobs.filter(j => j.status !== 'OPEN').length },
          ]}
          activeTab={activeFilter}
          onTabChange={(id) => setActiveFilter(id as 'open' | 'archived')}
        />

        {renderContent()}

        <JobEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          jobToEdit={jobToEdit}
          onSave={handleSaveJob}
          isSubmitting={loading}
        />

        <ConfirmationModal
          isOpen={!!jobToDelete}
          title="Delete Job"
          confirmText="Delete Job"
          onConfirm={handleConfirmDelete}
          onCancel={() => setJobToDelete(null)}
        >
          Are you sure you want to delete the job "{jobToDelete?.title}"? This action cannot be
          undone.
        </ConfirmationModal>

        <ErrorModal
          isOpen={isErrorModalOpen}
          title="Unable to Delete Job"
          message={errorMessage}
          onClose={() => setIsErrorModalOpen(false)}
        />
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
